import { useState, useMemo, useEffect } from 'react';
import { Stack, TopProject } from '../../types/globalTypes';
import { useSearchParams } from 'react-router-dom';

const variables = {
  primary: 'platforms',
  secondary: 'technologies',
  tertiary: 'regions',
  quaternary: 'domains',
  initial: 'All',
};

export const usePortfolioFilters = (stacks: Stack, projects: TopProject[]) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [selectedTechnologies, setSelectedTechnologies] = useState<string[]>(
    []
  );
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedDomains, setSelectedDomains] = useState<string[]>([]);

  useEffect(() => {
    const platforms = searchParams.get(variables.primary)?.split(',') || [
      variables.initial,
    ];
    const technologies = searchParams.get(variables.secondary)?.split(',') || [
      variables.initial,
    ];
    const regions = searchParams.get(variables.tertiary)?.split(',') || [
      variables.initial,
    ];
    const domains = searchParams.get(variables.quaternary)?.split(',') || [
      variables.initial,
    ];

    setSelectedPlatforms(platforms);
    setSelectedTechnologies(technologies);
    setSelectedRegions(regions);
    setSelectedDomains(domains);
  }, [searchParams]);

  const handlePlatformChange = (platforms: string[]) => {
    const newParams = new URLSearchParams(searchParams);
    if (platforms.length === 0 || platforms.includes(variables.initial)) {
      newParams.delete(variables.primary);
    } else {
      newParams.set(variables.primary, platforms.join(','));
    }
    newParams.delete(variables.secondary);
    setSearchParams(newParams, { replace: true });
  };

  const handleTechnologyChange = (technologies: string[]) => {
    const newParams = new URLSearchParams(searchParams);
    if (technologies.length === 0 || technologies.includes(variables.initial)) {
      newParams.delete(variables.secondary);
    } else {
      newParams.set(variables.secondary, technologies.join(','));
    }
    setSearchParams(newParams, { replace: true });
  };

  const handleRegionChange = (regions: string[]) => {
    const newParams = new URLSearchParams(searchParams);
    if (regions.length === 0 || regions.includes(variables.initial)) {
      newParams.delete(variables.tertiary);
    } else {
      newParams.set(variables.tertiary, regions.join(','));
    }
    setSearchParams(newParams, { replace: true });
  };

  const handleDomainChange = (domains: string[]) => {
    const newParams = new URLSearchParams(searchParams);
    if (domains.length === 0 || domains.includes(variables.initial)) {
      newParams.delete(variables.quaternary);
    } else {
      newParams.set(variables.quaternary, domains.join(','));
    }
    setSearchParams(newParams, { replace: true });
  };

  const projectsFilteredByPlatformAndTech = useMemo(() => {
    if (!projects) return [];
    return projects.filter(project => {
      const projectPlatforms = project.Platforms.map(p => p.Name);
      const projectTechnologies = project.Technologies.map(t => t.Name);
      const platformMatch =
        selectedPlatforms.includes(variables.initial) ||
        selectedPlatforms.some(platform => projectPlatforms.includes(platform));
      const technologyMatch =
        selectedTechnologies.includes(variables.initial) ||
        selectedTechnologies.some(tech => projectTechnologies.includes(tech));
      return platformMatch && technologyMatch;
    });
  }, [projects, selectedPlatforms, selectedTechnologies]);

  const availableDomains = useMemo(() => {
    const domainSet = new Set<string>([variables.initial]);
    projectsFilteredByPlatformAndTech.forEach(project => {
      if (project.Domain?.Name) {
        domainSet.add(project.Domain.Name);
      }
    });
    return Array.from(domainSet);
  }, [projectsFilteredByPlatformAndTech]);

  const projectsFilteredByDomain = useMemo(() => {
    return projectsFilteredByPlatformAndTech.filter(project => {
      if (selectedDomains.includes(variables.initial)) {
        return true;
      }
      const projectDomain = project.Domain?.Name;
      return projectDomain ? selectedDomains.includes(projectDomain) : false;
    });
  }, [projectsFilteredByPlatformAndTech, selectedDomains]);

  const regions = useMemo(() => {
    const regionSet = new Set<string>([variables.initial]);
    projectsFilteredByDomain.forEach(project => {
      if (project.Region) {
        regionSet.add(project.Region);
      }
    });
    return Array.from(regionSet);
  }, [projectsFilteredByDomain]);

  useEffect(() => {
    const areSelectedRegionsValid = selectedRegions.every(region =>
      regions.includes(region)
    );
    if (
      !areSelectedRegionsValid &&
      !selectedRegions.includes(variables.initial)
    ) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete(variables.tertiary);
      setSearchParams(newParams, { replace: true });
    }
  }, [regions, selectedRegions, searchParams, setSearchParams]);

  useEffect(() => {
    const areSelectedDomainsValid = selectedDomains.every(domain =>
      availableDomains.includes(domain)
    );
    if (
      !areSelectedDomainsValid &&
      !selectedDomains.includes(variables.initial)
    ) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete(variables.quaternary);
      setSearchParams(newParams, { replace: true });
    }
  }, [availableDomains, selectedDomains, searchParams, setSearchParams]);

  const platforms = useMemo(() => {
    let platformsList = [variables.initial];
    if (!stacks) {
      return platformsList;
    }
    platformsList.push(...Object.keys(stacks));
    return platformsList;
  }, [stacks]);

  const availableTechnologies = useMemo(() => {
    if (!projects) return new Set<string>();
    const techs = new Set<string>();
    projects.forEach(project => {
      project.Technologies.forEach(tech => {
        techs.add(tech.Name);
      });
    });
    return techs;
  }, [projects]);

  const technologies = useMemo(() => {
    if (!projects || !stacks) return [variables.initial];
    if (selectedPlatforms.includes(variables.initial)) {
      const allTechnologies = new Set<string>([variables.initial]);
      Object.values(stacks).forEach(techs => {
        techs.forEach(tech => allTechnologies.add(tech));
      });
      return Array.from(allTechnologies).filter(
        tech => tech === variables.initial || availableTechnologies.has(tech)
      );
    }
    const platformTechnologies = new Set<string>();
    selectedPlatforms.forEach(platform => {
      const techs = stacks[platform] || [];
      techs.forEach(tech => platformTechnologies.add(tech));
    });

    const matchingProjects = projects.filter(project => {
      const projectPlatforms = project.Platforms.map(p => p.Name);
      return selectedPlatforms.some(platform =>
        projectPlatforms.includes(platform)
      );
    });
    const usedTechnologies = new Set<string>([variables.initial]);
    matchingProjects.forEach(project => {
      project.Technologies.forEach(tech => {
        if (platformTechnologies.has(tech.Name)) {
          usedTechnologies.add(tech.Name);
        }
      });
    });
    return Array.from(usedTechnologies);
  }, [projects, stacks, selectedPlatforms, availableTechnologies]);

  const filteredProjects = useMemo(() => {
    return projectsFilteredByDomain.filter(project => {
      const projectRegion = project.Region || '';
      const regionMatch =
        selectedRegions.includes(variables.initial) ||
        selectedRegions.includes(projectRegion);
      return regionMatch;
    });
  }, [projectsFilteredByDomain, selectedRegions]);

  return {
    platforms,
    technologies,
    regions,
    domains: availableDomains,
    selectedRegions,
    selectedDomains,
    selectedPlatforms,
    selectedTechnologies,
    filteredProjects,
    handlePlatformChange,
    handleTechnologyChange,
    handleRegionChange,
    handleDomainChange,
  };
};
